import { PERSON_NAME_PATTERN, PERSON_NAME_ERROR } from "../Helper/personNameValidation";

export const authContactSchema = {
  title: "Authorized Contacts",
  type: "object",
  required: ["contacts"],
  properties: {
    contacts: {
      type: "array",
      title: "Contacts",
      description: "Add contacts for communication and emergencies",
      minItems: 1,
      errorMessage: {
        minItems: "At least one authorized contact is required",
      },
      items: {
        type: "object",
        required: [
          "contactFirstName",
          "contactLastName",
          "contactRelationship",
          "contactRoles",
          "contactPhoneNumber",
          "contactEmail",
        ],
        properties: {
          contactFirstName: {
            type: "string",
            title: "First Name",
            minLength: 2,
            maxLength: 25,
            pattern: PERSON_NAME_PATTERN,
            errorMessage: {
              required: "First name is required",
              minLength: "First name must be at least 2 characters",
              maxLength: "First name must be under 25 characters",
              pattern: PERSON_NAME_ERROR,
            },
          },
          contactLastName: {
            type: "string",
            title: "Last Name",
            minLength: 2,
            maxLength: 25,
            pattern: PERSON_NAME_PATTERN,
            errorMessage: {
              required: "Last name is required",
              minLength: "Last name must be at least 2 characters",
              maxLength: "Last name must be under 25 characters",
              pattern: PERSON_NAME_ERROR,
            },
          },
          contactRelationship: {
            type: "string",
            title: "Relationship",
            minLength: 1,
            enum: [
              "FATHER", "MOTHER", "FATHER-IN-LAW", "MOTHER-IN-LAW", "SPOUSE",
              "BROTHER", "SISTER", "SON", "SON-IN-LAW", "DAUGHTER", "DAUGHTER-IN-LAW",
              "GRANDSON", "GRANDDAUGHTER", "DOCTOR", "SOCIAL_WORKER", "DISCHARGE_PLANNER",
              "GERIATRIC_CARE_MANAGER", "NURSE", "FRIEND", "NEIGHBOR", "INSURANCE_COMPANY", "OTHER"
            ],
            errorMessage: {
              required: "Relationship is required",
              minLength: "Relationship is required"
            }
          },
          contactRoles: {
            type: "array",
            title: "Contact Roles",
            uniqueItems: true,
            minItems: 1,
            items: {
              type: "string",
              enum: ["primary", "emergency", "medical", "payer"],
            },
            errorMessage: {
              required: "At least one role is required",
              minItems: "At least one role must be selected",
            },
          },
          contactPhoneNumber: {
            type: "string",
            title: "Phone Number",
            pattern: "^\\(\\d{3}\\) \\d{3}-\\d{4}$",
            errorMessage: {
              required: "Phone number is required",
              pattern: "Enter a valid phone number in format (xxx) xxx-xxxx",
            },
          },
          contactEmail: {
            type: "string",
            title: "Email",
            format: "email",
            maxLength: 85,
            errorMessage: {
              required: "Email is required",
              format: "Please enter a valid email address",
              maxLength: "Email must be under 85 characters",
            },
          },
        },
        dependencies: {
          contactRelationship: {
            oneOf: [
              {
                properties: {
                  contactRelationship: {
                    enum: [
                      "DOCTOR", "SOCIAL_WORKER", "DISCHARGE_PLANNER",
                      "GERIATRIC_CARE_MANAGER", "NURSE"
                    ]
                  },
                  facilityAddress: {
                    type: "string", title: "Address", minLength: 2, maxLength: 100, errorMessage: {
                      required: "Address is required",
                      minLength: "Please address at least 2 characters",
                      maxLength: "Please keep it under 100 characters",
                    },
                  },
                  city: {
                    type: "string", title: "City", maxLength: 30, errorMessage: {
                      required: "City is required",
                      maxLength: "City must be under 30 characters",
                    },
                  },
                  state: {
                    type: "string", title: "State", maxLength: 100, errorMessage: {
                      required: "State is required",
                      maxLength: "State must be under 100 characters",
                    },
                  },
                  zipCode: {
                    type: "string",
                    title: "ZIP Code",
                    pattern: "^[0-9]{5}$",
                    errorMessage: {
                      required: "Zip code is required",
                      pattern: "ZIP code must be exactly 5 digits",
                    },
                  }
                },
              },
              {
                properties: {
                  contactRelationship: {
                    enum: [
                      "FATHER", "MOTHER", "FATHER-IN-LAW", "MOTHER-IN-LAW", "SPOUSE",
                      "BROTHER", "SISTER", "SON", "SON-IN-LAW", "DAUGHTER", "DAUGHTER-IN-LAW",
                      "GRANDSON", "GRANDDAUGHTER", "FRIEND", "NEIGHBOR",
                      "INSURANCE_COMPANY", "OTHER"
                    ]
                  }
                }
              },
              {
                properties: {
                  contactRelationship: { const: "OTHER" },
                  otherSpecify: {
                    type: "string",
                    title: "Other Specify",
                    minLength: 2,
                    maxLength: 100,
                    errorMessage: {
                      minLength: "Please specify at least 2 characters",
                      maxLength: "Please keep it under 100 characters",
                      required: "Other specify is required"
                    },
                  },
                },
                required: ["otherSpecify"],
              }
            ]
          }
        }

      }
    }
  }
};

export const authContactUiSchema = {
  contacts: {
    "ui:field": "CollapsibleContactField",
    items: {
      "ui:order": [
        "contactFirstName",
        "contactLastName",
        "contactRelationship",
        "contactRoles",
        "otherSpecify",
        "contactPhoneNumber",
        "contactEmail",
        "facilityAddress",
        "city",
        "state",
        "zipCode",
      ],

      contactFirstName: { "ui:md": 6, "ui:widget": "PersonNameWidget" },
      contactLastName: { "ui:md": 6, "ui:widget": "PersonNameWidget" },
      facilityAddress: { "ui:md": 6 },
      state: {
        "ui:md": 6, "ui:field": "StateField",
        "ui:options": {
          hideError: true,
        },
      },
      city: { "ui:md": 6 },
      zipCode: {
        "ui:md": 6, "ui:widget": "NumberWidget",
        "ui:options": {
          hideError: false,
          digitsOnly: true,
          maxLength: 5,
          inputMode: "numeric"
        }
      },

      contactRelationship: {
        "ui:widget": "RelationshipSelect", "ui:md": 6, "ui:options": {
          hideError: false,
        }
      },
      contactRoles: {
        "ui:widget": "MultiSelectDropdown", // Enables multi-select as checkboxes
        "ui:enumNames": [
          "Primary Contact",
          "Emergency Contact",
          "Medical Power of Attorney",
          "Payer",
        ],
        "ui:md": 6,
      },
      otherSpecify: {
        "ui:placeholder": "Enter other relationship",
        "ui:md": 6,
      },
      contactPhoneNumber: {
        "ui:md": 6, "ui:widget": "PhoneWidget", "ui:options": {
          inputType: "tel",
          disabled: false,
          readonly: false,
          hideError: false,
        },
      },
      contactEmail: { "ui:md": 6 },
    },
  },
};
